<template>
    <div class="d-flex" style="gap: 20px;">
        <div v-if="searchMode" class="flex-fill">
            <SearchComponent v-model="selectedPerson" label="Search" :search-u-r-l="strUrl" />
        </div>
        <slot name="left-side" />
        <div v-if="!searchMode && namedPerson && showTitle">
            <fg-input v-model="namedPerson.title" label="Title" :disabled="disableFields" />
        </div>
        <div v-if="!searchMode" class="flex-fill">
            <fg-input v-if="mode === 'string' || !namedPerson?.id" v-model="personName" label="Name"
                :disabled="disableFields && !selectedPerson" required />

            <div v-if="mode === 'components'" class="d-flex flex-wrap" style="gap: 5px;">
                <div style="flex-grow: 3; min-width: 200px;">
                    <fg-input v-model="personLastName" label="Last Name" :disabled="disableFields && !selectedPerson"
                        required :error="(showErrors && !personLastName.trim()) ? 'Last Name is Required' : ''" />
                </div>
                <div style="flex-grow: 2; min-width: 200px;">
                    <fg-input v-model="personFirstName" label="First Name" :disabled="disableFields && !selectedPerson"
                        required :error="(showErrors && !personFirstName.trim()) ? 'First Name is Required' : ''" />
                </div>
                <div style="min-width: 100px;">
                    <fg-input v-model="personMiddleName" label="Middle Name"
                        :disabled="disableFields && !selectedPerson" />
                </div>
            </div>
        </div>
        <div v-if="namedPerson && !searchMode && showSuffix">
            <fg-input v-model="namedPerson.suffix" label="Suffix" :disabled="disableFields" />
        </div>
        <slot name="default" />
    </div>
</template>

<script lang="ts">
import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import { Person } from '@/models/Person'
import { Patient } from '@/models/Patient'
import { Vendor } from '@/models/Vendor'
import { Prescriber } from '@/models/Prescriber'
import SearchComponent from '@/components/SearchComponent.vue';

@Component({
    name: "NameSearchComponent",
    components: {
        SearchComponent
    }
})
export default class NameSearchComponent extends Vue {
    @Prop() private value!: any;
    @Prop() private namedPerson!: Person | null;
    @Prop() private disableFields!: boolean;
    @Prop({ type: Boolean, default: false }) private searchMode!: boolean;
    @Prop({ default: false }) private showErrors!: boolean;
    @Prop({ default: true }) private showTitle: boolean | undefined;
    @Prop({ default: true }) private showSuffix: boolean | undefined;

    created() {
        this.updateLocalNameFromComponents();
    }

    @Watch("namedPerson")
    updatePerson() {
        this.updateLocalNameFromComponents();
    }

    get mode() {
        const isComponentType = this.namedPerson instanceof Patient ||
            this.namedPerson instanceof Prescriber;
        return isComponentType ? "components" : "string";
    }

    get strUrl() {
        if (this.namedPerson instanceof Patient)
            return "/Patient"
        else if (this.namedPerson instanceof Prescriber)
            return "/Prescriber"
        else if (this.namedPerson instanceof Vendor)
            return "/Vendor"
        return "";
    }

    private localName = "";

    get personName(): string {
        if (this.mode === "string") {
            return this.value;
        }

        return this.localName;
    }
    set personName(val: string) {
        if (this.mode === "string") {
            this.$emit('input', val);
            return;
        }

        this.localName = val;

        let lastName = "";
        let firstName = "";
        let middleName = "";

        const trimmedVal = val.trim();

        if (trimmedVal) {
            if (trimmedVal.includes(",")) {
                const commaIndex = trimmedVal.indexOf(',');
                lastName = trimmedVal.substring(0, commaIndex).trim();

                const namesAfterComma = trimmedVal.substring(commaIndex + 1)
                    .trim()
                    .split(' ')
                    .filter(Boolean);

                if (namesAfterComma.length) {
                    firstName = namesAfterComma[0];
                    middleName = namesAfterComma.slice(1).join(' ');
                }
            } else {
                const nameParts = trimmedVal.split(' ').filter(Boolean);

                if (nameParts.length === 1) {
                    lastName = nameParts[0];
                } else if (nameParts.length === 2) {
                    firstName = nameParts[0];
                    lastName = nameParts[1];
                } else {
                    firstName = nameParts[0];
                    lastName = nameParts[nameParts.length - 1];
                    middleName = nameParts.slice(1, -1).join(' ');
                }
            }
        }

        this.$emit('input', { lastName, middleName, firstName });
    }

    get personNameComponents() {
        return {
            firstName: this.personFirstName?.trim() ?? "",
            middleName: this.personMiddleName?.trim() ?? "",
            lastName: this.personLastName?.trim() ?? ""
        };
    }

    get personFirstName(): string {
        return this.value.firstName;
    }
    set personFirstName(val: string) {
        this.$emit('input', {
            ...this.personNameComponents,
            firstName: val
        });
        this.updateLocalNameFromComponents();
    }

    get personMiddleName(): string {
        return this.value.middleName;
    }
    set personMiddleName(val: string) {
        this.$emit('input', {
            ...this.personNameComponents,
            middleName: val
        });
        this.updateLocalNameFromComponents();
    }

    get personLastName(): string {
        return this.value.lastName;
    }
    set personLastName(val: string) {
        this.$emit('input', {
            ...this.personNameComponents,
            lastName: val
        });
        this.updateLocalNameFromComponents();
    }

    updateLocalNameFromComponents() {
        this.$nextTick(() => {
            const name = [];

            const { firstName, middleName, lastName } = this.personNameComponents;

            if (firstName) name.push(firstName);
            if (middleName) name.push(middleName);
            if (lastName) name.push(lastName);

            this.localName = name.join(" ");
        });
    }

    get selectedPerson(): Person | null {
        return this.namedPerson;
    }
    set selectedPerson(value: Person | null) {
        this.$emit('person-update', value);
    }
}

</script>

<style scoped>
.name-components {}
</style>